
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { User, Session, Subscription } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContextType } from '@/types/auth';
import { useProfileManager } from '@/hooks/useProfileManager';
import { authOperations } from '@/services/authOperations';
import { debug } from '@/utils/debug';
import { useToast } from '@/hooks/use-toast';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Use profile manager hook
  const { profile, fetchProfile, clearProfile } = useProfileManager();

  // Use refs to track state and prevent unnecessary setups
  const isInitialized = useRef(false);
  const subscriptionRef = useRef<Subscription | null>(null);
  const isMountedRef = useRef(true);

  // Profile fetch timeout and retry state
  const PROFILE_FETCH_TIMEOUT = 10000; // 10 seconds
  const MAX_PROFILE_RETRIES = 3;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const profileRetryCount = useRef(0);

  // Enhanced handleUserChange with timeout protection and retry logic
  const handleUserChange = useCallback(async (newUser: User | null) => {
    if (newUser && (!user || user.id !== newUser.id)) {
      debug.log('🔥 New user detected, fetching profile:', newUser.id);
      setUser(newUser);
      // Keep loading true until profile is fetched
      setLoading(true);

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Set timeout to prevent infinite loading
      timeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && loading) {
          debug.error('🔥 Profile fetch timeout - forcing loading to false');
          setLoading(false);
          toast({
            title: "Connection Issue",
            description: "Having trouble loading your profile. Please refresh the page.",
            variant: "destructive"
          });
        }
      }, PROFILE_FETCH_TIMEOUT);

      // Fetch profile with retry logic
      const attemptFetch = async (retryCount: number) => {
        if (!isMountedRef.current) return;

        try {
          await fetchProfile(newUser.id);
          // Success - clear timeout and reset retry count
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          profileRetryCount.current = 0;
          debug.log('🔥 Profile fetched successfully');
        } catch (error) {
          debug.error('🔥 Profile fetch failed:', error);
          profileRetryCount.current = retryCount + 1;

          if (profileRetryCount.current <= MAX_PROFILE_RETRIES) {
            debug.log(`🔥 Retrying profile fetch (${profileRetryCount.current}/${MAX_PROFILE_RETRIES})`);
            setTimeout(() => {
              if (isMountedRef.current) {
                attemptFetch(profileRetryCount.current);
              }
            }, 1000 * profileRetryCount.current); // Exponential backoff
          } else {
            debug.error('🔥 Profile fetch failed after all retries');
            setLoading(false);
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            toast({
              title: "Profile Load Error",
              description: "Unable to load your profile. Please try signing in again.",
              variant: "destructive"
            });
          }
        }
      };

      // Start fetch with a small delay to prevent blocking
      setTimeout(() => {
        if (isMountedRef.current) {
          attemptFetch(0);
        }
      }, 0);
    } else if (!newUser) {
      debug.log('🔥 No user, clearing state');
      setUser(null);
      clearProfile();
      setLoading(false);
      // Clear timeout when signing out
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      profileRetryCount.current = 0;
    }
  }, [user?.id, fetchProfile, clearProfile, toast]); // Include toast in dependencies

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitialized.current) {
      debug.log('🔥 AuthContext already initialized, skipping');
      return;
    }

    isMountedRef.current = true;
    let mounted = true;
    debug.log('🔥 Setting up auth state listener (ONCE)...');

    // Clean up any existing subscription
    if (subscriptionRef.current) {
      debug.log('🔥 Cleaning up existing subscription');
      subscriptionRef.current.unsubscribe();
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        debug.log('🔥 Auth State Change Event:', event);
        debug.log('🔥 Session Object:', session);

        if (!mounted) {
          debug.log('🔥 Component unmounted, skipping auth state change');
          return;
        }

        try {
          // Update session state
          setSession(session);

          // Handle user change with deferred profile fetching
          // loading will be set to false when profile is fetched (see useEffect below)
          if (session?.user) {
            handleUserChange(session.user);
          } else {
            handleUserChange(null);
          }
        } catch (error) {
          debug.error('🔥 ERROR in auth state change handler:', error);

          // Check for refresh token errors
          const errorMessage = error instanceof Error ? error.message : String(error);
          if (
            errorMessage.includes('Invalid Refresh Token') ||
            errorMessage.includes('Refresh Token Not Found') ||
            errorMessage.includes('not found') // Catch generic "not found" which often relates to sessions
          ) {
            debug.log('🔥 Critical auth error detected, forcing sign out to clear state');
            // Force clear storage and state
            localStorage.clear(); // Aggressive clear to ensure bad tokens are gone
            setUser(null);
            setSession(null);
            clearProfile();
          }

          setLoading(false);
        }
      }
    );

    subscriptionRef.current = subscription;

    // Check for existing session only once
    debug.log('🔥 Checking initial session...');
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!mounted) return;

      if (error) {
        throw error;
      }

      debug.log('🔥 Initial session check:', session?.user?.id);
      setSession(session);
      if (session?.user) {
        handleUserChange(session.user);
        // loading will be set to false when profile is fetched (see useEffect below)
      } else {
        setLoading(false);
      }
    }).catch((error) => {
      debug.error('🔥 ERROR in initial session check:', error);

      // Handle refresh token errors on initial load
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (
        errorMessage.includes('Invalid Refresh Token') ||
        errorMessage.includes('Refresh Token Not Found')
      ) {
        debug.log('🔥 Critical auth error on init, clearing state');
        localStorage.clear();
        setUser(null);
        setSession(null);
        clearProfile();
      }

      setLoading(false);
    });

    isInitialized.current = true;

    return () => {
      debug.log('🔥 Cleaning up auth state listener...');
      mounted = false;
      isMountedRef.current = false;
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, []); // Empty dependency array to prevent re-initialization

  // Wrap auth operations with profile cache clearing
  const signOut = useCallback(async () => {
    clearProfile();
    await authOperations.signOut();
  }, [clearProfile]);

  // Enhanced logging for super admin debugging
  // FIXED: Also set loading to false when there's an error (user exists but no profile after timeout)
  useEffect(() => {
    if (profile) {
      debug.log('🔥 AuthContext - Profile loaded:', {
        id: profile.id,
        email: profile.email,
        role: profile.role,
        is_active: profile.is_active
      });

      if (profile.role === 'super_admin') {
        debug.log('🔥 SUPER ADMIN detected in AuthContext!');
      }
      // Set loading to false when profile is successfully fetched
      setLoading(false);
      // Clear timeout on success
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    } else if (user && !profile && !loading) {
      // User exists but no profile - this might be an error state
      // The loading state should have been cleared by the timeout or error handler
      debug.warn('🔥 User exists but no profile - auth may be in error state');
    }
  }, [profile, user, loading]); // Add user and loading to dependencies

  // Memoize the context value with stable references
  const value = useMemo(() => ({
    user,
    profile,
    session,
    loading,
    signIn: authOperations.signIn,
    signUp: authOperations.signUp,
    signOut,
    resetPassword: authOperations.resetPassword,
  }), [user, profile, session, loading, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
